// recova GmbH – Kartenansicht der Tagesrouten (OpenStreetMap/Leaflet): Marker je Einsatz, Farbe je Techniker, Routenlinien in Reihenfolge.
import { useEffect, useState, useCallback } from "react";
import { API, fmtErr, STATUS_LABELS } from "@/lib/api";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Route as RouteIcon } from "lucide-react";
import { toast } from "sonner";

const TECH_COLORS = ["#028090", "#e76f51", "#7c5cbf", "#2a9d8f", "#d4a017", "#c2185b"];

// Rundes Farb-Marker-Icon mit Stopp-Nummer (ohne Bild-Assets).
const dotIcon = (color, n) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45)">${n}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

export const RouteMap = () => {
  const iso = (d) => {
    const x = new Date(d);
    x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
    return x.toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(() => iso(new Date()));
  const [jobs, setJobs] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(() => {
    API.get("/dispatch/map", { params: { date } })
      .then((r) => setJobs(r.data))
      .catch((e) => toast.error(fmtErr(e)));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const optimize = async () => {
    setOptimizing(true);
    try {
      const r = await API.post("/dispatch/optimize", null, { params: { date } });
      setLastResult(r.data);
      toast.success(`Route optimiert – ${r.data.assigned.length} Stopp(s) neu sortiert`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setOptimizing(false);
    }
  };

  const list = jobs || [];
  const withCoords = list.filter((j) => j.lat != null && j.lon != null);
  const noCoords = list.filter((j) => j.lat == null || j.lon == null);
  const techNames = [...new Set(list.map((j) => j.assigned_to_name || "Nicht zugewiesen"))];
  const colorOf = (name) => TECH_COLORS[techNames.indexOf(name) % TECH_COLORS.length];
  const center = withCoords.length ? [withCoords[0].lat, withCoords[0].lon] : [50.954, 6.64];

  // Routenlinien: pro Techniker die Stopps in route_order-Reihenfolge verbinden
  const routes = techNames.map((t) => {
    const stops = withCoords
      .filter((j) => (j.assigned_to_name || "Nicht zugewiesen") === t)
      .sort((a, b) => (a.route_order || 0) - (b.route_order || 0));
    return { tech: t, points: stops.map((j) => [j.lat, j.lon]) };
  });

  return (
    <div className="space-y-4" data-testid="route-map-panel">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div>
          <Label>Datum</Label>
          <Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="map-date" />
        </div>
        <button onClick={optimize} disabled={optimizing || !withCoords.length} data-testid="map-optimize"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#1b4332] px-3.5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#143326] disabled:opacity-40">
          <RouteIcon className="h-4 w-4" /> {optimizing ? "Optimiere…" : "Route optimieren"}
        </button>
        <div className="flex flex-wrap items-center gap-2" data-testid="map-legend">
          {techNames.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600" data-testid={`map-legend-${t.replace(/\s+/g, "-")}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf(t) }} />
              {t}
            </span>
          ))}
          {list.length === 0 && jobs !== null && <span className="text-sm text-slate-400">Keine Einsätze an diesem Tag.</span>}
        </div>
      </div>

      {lastResult && (
        <div className="rounded-xl border border-[#028090]/30 bg-cyan-50/60 p-3 text-xs text-[#0f172a]" data-testid="map-optimize-result">
          <p className="font-bold text-[#028090]">Optimierte Reihenfolge (ab Standort Bergheim):</p>
          {lastResult.assigned.map((r) => (
            <p key={r.order_id} className="mt-0.5" data-testid={`optimize-stop-${r.order_number}`}>
              {r.route_order}. {r.order_number} · {r.zip} · {r.technician}
            </p>
          ))}
          {(lastResult.routes || []).map((rt) => (
            <p key={rt.technician} className="mt-1.5 font-semibold text-[#1b4332]" data-testid={`route-stats-${rt.technician.replace(/\s+/g, "-")}`}>
              {rt.technician}: {rt.stops} Stopp(s)
              {rt.distance_km != null ? ` · ca. ${rt.distance_km} km · ${rt.duration_min} Min Fahrzeit` : " · Fahrzeit nicht ermittelbar"}
            </p>
          ))}
          {lastResult.total_distance_km != null && (
            <p className="mt-1 border-t border-[#028090]/20 pt-1 font-bold" data-testid="route-stats-total">
              Gesamt: ca. {lastResult.total_distance_km} km · {lastResult.total_duration_min} Min Fahrzeit
            </p>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm" data-testid="route-map">
        <MapContainer center={center} zoom={11} style={{ height: "480px", width: "100%" }} key={`${date}-${withCoords.length}`}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {routes.filter((r) => r.points.length > 1).map((r) => (
            <Polyline key={r.tech} positions={r.points} pathOptions={{ color: colorOf(r.tech), weight: 4, opacity: 0.7, dashArray: "1 6" }} />
          ))}
          {withCoords.map((j) => {
            const tech = j.assigned_to_name || "Nicht zugewiesen";
            const num = (j.route_order || 0) > 0 ? j.route_order : "•";
            return (
              <Marker key={j.id} position={[j.lat, j.lon]} icon={dotIcon(colorOf(tech), num)}>
                <Popup>
                  <div style={{ fontSize: 13 }} data-testid={`map-popup-${j.order_number}`}>
                    <strong>{j.order_number}</strong> – {j.customer?.name}<br />
                    {j.customer?.street}, {j.customer?.zip} {j.customer?.city}<br />
                    {j.appliance?.brand} {j.appliance?.type_label} · {j.time_window || "Zeit offen"}<br />
                    {STATUS_LABELS[j.status] || j.status} · {tech}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {noCoords.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="map-no-coords">
          <MapPin className="mr-1 inline h-3.5 w-3.5" />
          Ohne Kartendarstellung (Adresse nicht gefunden): {noCoords.map((j) => j.order_number).join(", ")}
        </div>
      )}
    </div>
  );
};
